import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { TabPanel, TabView } from 'primereact/tabview';
import { flagHistoryApi, flagLookupsApi, memberFlagsApi } from '../../api/flagSystemApi';
import {
  flagError, flagFieldErrors, formatFlagInstant, formatLegacyScan,
  memberFlagView, validateMemberChange,
} from '../../utils/flagSystem';
import { Feedback, Field, FlagPage, ResourceState, Unavailable, useRemoteResource } from './FlagSystemCommon';
import { flagApplicabilityLabel, flagApplicabilityValue, hasFlagApplicability } from '../../utils/flagApplicability';

const MemberAssignments = ({ account, scope, memberId, catalog, onAccessError }) => {
  const loader = useCallback(() => memberFlagsApi.list(scope, memberId), [scope, memberId]);
  const resource = useRemoteResource(loader, onAccessError);
  const [dialog, setDialog] = useState(null);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [mustReload, setMustReload] = useState(false);
  const assignments = resource.data || [];
  const options = (catalog.data || []).filter(flag => flag.active !== false)
    .map(flag => memberFlagView(flag, account.isCustomer))
    .filter(flag => flag.canAddRemove && !assignments.some(assignment => assignment.flag.flagId === flag.flagId));
  const open = (assignment) => { setDialog(assignment ? { assignment } : {}); setComment(''); setErrors({}); setError(''); setMessage(''); };
  const close = () => { if (!saving) { setDialog(null); setComment(''); setErrors({}); setError(''); } };
  const reload = () => { setMustReload(false); setError(''); resource.reload(); catalog.reload(); };
  const submit = async () => {
    const validation = validateMemberChange(memberId, comment, account.isCustomer);
    if (!dialog.assignment && !options.some(flag => flag.flagId === selectedFlag)) validation.flagId = 'Select an available flag.';
    setErrors(validation);
    if (Object.keys(validation).length || mustReload) return;
    setSaving(true); setError('');
    try {
      if (dialog.assignment) await memberFlagsApi.remove(scope, dialog.assignment.memberFlagId, comment);
      else await memberFlagsApi.assign(scope, { memberId, flagId: selectedFlag, comment });
      setMessage(dialog.assignment ? 'Flag removed. Its assignment history is retained by the server.' : 'Flag assigned.');
      setDialog(null); setComment(''); setSelectedFlag(null); resource.reload(); catalog.reload();
    } catch (failure) {
      onAccessError(failure); setError(flagError(failure, 'Unable to change the member assignment.'));
      setErrors(flagFieldErrors(failure));
      if (!failure.response || failure.response.status === 409) setMustReload(true);
    } finally { setSaving(false); }
  };
  return <div className="flag-member-card">
    <div className="flex align-items-center justify-content-between gap-3 mb-3">
      <h5 className="mb-0">Member ID: {memberId}</h5>
      <Button label="Reload member flags" icon="pi pi-refresh" className="p-button-text" disabled={saving} onClick={reload} />
    </div>
    <Feedback error={!dialog ? error : ''} message={message} />
    <p className="flag-note">This is an exact identifier lookup, not membership verification or an access decision.</p>
    <ResourceState resource={resource}>
      {!assignments.length && <Message severity="info" text="No current flags found for this Member ID. This may mean no flag-system record, no active assignments, or disabled definitions; it does not establish membership or access eligibility." />}
      {assignments.map(assignment => {
        const flag = memberFlagView(assignment.flag, account.isCustomer);
        return <div className="flag-member-card" key={assignment.memberFlagId}>
          <div className="flex align-items-center justify-content-between flex-wrap gap-3">
            <strong>{flag.name}</strong>
            {hasFlagApplicability(assignment.flag) && <Tag value={flagApplicabilityLabel(assignment.flag)}
              severity={flagApplicabilityValue(assignment.flag) ? 'info' : 'warning'} />}
            <div className="flex gap-2">
              {flag.canAddRemove && <Button label="Remove Flag" icon="pi pi-minus" className="p-button-danger p-button-text"
                disabled={saving || mustReload} onClick={() => open(assignment)} />}
              {flag.canOverride && <Button label="Override unavailable" icon="pi pi-lock" className="p-button-warning" disabled
                aria-describedby="override-dependency" />}
            </div>
          </div>
          {flag.description && <p>{flag.description}</p>}
          {flag.action && <p>{flag.action}</p>}
          {account.isCustomer && <div className="flag-note">
            {[['juniorAction', 'Junior Local User'], ['dutyManagerAction', 'Duty Manager'], ['seniorAction', 'Senior Local User']]
              .map(([field, label]) => assignment.flag[field] && <p key={field}><strong>{label}:</strong> {assignment.flag[field]}</p>)}
          </div>}
          <small className="text-color-secondary">Assigned {formatFlagInstant(assignment.addedAt)} (British time)</small>
        </div>;
      })}
    </ResourceState>
    <ResourceState resource={catalog}>
      <div className="flag-member-search mt-3">
        <Field id="member-flag-choice" label="Flag to assign">
          <Dropdown inputId="member-flag-choice" options={options} optionLabel="name" optionValue="flagId"
            value={options.some(flag => flag.flagId === selectedFlag) ? selectedFlag : null}
            onChange={event => setSelectedFlag(event.value)} placeholder="Select an available flag"
            disabled={saving || resource.loading || Boolean(resource.error) || mustReload || !options.length} />
        </Field>
        <Button label="Assign Flag" icon="pi pi-plus" className="p-button-success" onClick={() => open(null)}
          disabled={saving || resource.loading || Boolean(resource.error) || mustReload || !options.some(flag => flag.flagId === selectedFlag)} />
      </div>
      {!options.length && <p className="flag-note">No unassigned flags are available for this account. Only active flags you may add/remove are offered.</p>}
    </ResourceState>
    <div id="override-dependency"><Unavailable>A verified, customer/site-authorized numeric accessPointId lookup is missing. Override submission and override lists remain unavailable. Tile strings are not numeric access-point IDs. An override does not itself perform check-in or physical access.</Unavailable></div>
    <Unavailable>Current access decisions, manual check-in, attendance and physical gate actions are not available.</Unavailable>
    {account.isCustomer ? <p className="flag-note">Review recorded flag-assignment activity in the{' '}
      <Link to={`/support/flags/audit-log?memberId=${encodeURIComponent(memberId)}`}>Audit Log</Link>.</p> :
      <Unavailable>Assignment-history browsing is not available for site roles.</Unavailable>}
    <Dialog header={dialog && dialog.assignment ? 'Remove Flag' : 'Assign Flag'} visible={Boolean(dialog)}
      className="flag-editor" modal closable={!saving} onHide={close}
      footer={<><Button label="Cancel" className="p-button-text" disabled={saving} onClick={close} />
        <Button label="Confirm" icon="pi pi-check" loading={saving}
          disabled={saving || mustReload || resource.loading || catalog.loading || Boolean(resource.error) || Boolean(catalog.error)} onClick={submit} /></>}>
      <Feedback error={error} />
      {mustReload && <><Message severity="warn" text="Reload current assignments before repeating this request. A network failure may have occurred after the server completed the change." />
        <Button label="Reload current data" className="p-button-text" onClick={reload} disabled={saving} /></>}
      {errors.flagId && <Message severity="error" text={errors.flagId} />}
      <p>Member ID: <strong>{memberId}</strong></p>
      <p>{dialog && dialog.assignment ? dialog.assignment.flag.name : (options.find(flag => flag.flagId === selectedFlag) || {}).name}</p>
      <Field id="member-flag-comment" label={`Comment${account.isCustomer ? ' (optional)' : ' (required)'}`} error={errors.comment}>
        <InputTextarea id="member-flag-comment" value={comment} maxLength={1000} rows={3} disabled={saving}
          onChange={event => setComment(event.target.value)} />
      </Field>
    </Dialog>
  </div>;
};

const MemberSearch = ({ account, scope, onAccessError }) => {
  const [input, setInput] = useState('');
  const [memberId, setMemberId] = useState(null);
  const [lookupVersion, setLookupVersion] = useState(0);
  const [error, setError] = useState('');
  const loader = useCallback(() => memberFlagsApi.catalog(scope), [scope]);
  const catalog = useRemoteResource(loader, onAccessError);
  const search = (event) => {
    event.preventDefault();
    const validation = validateMemberChange(input, '', true);
    if (validation.memberId) { setError(validation.memberId); setMemberId(null); return; }
    setError(''); setMemberId(input.trim()); setLookupVersion(value => value + 1);
  };
  return <>
    <form className="flag-member-search" onSubmit={search}>
      <Field id="flag-member-id" label="Member ID" error={error}>
        <InputText id="flag-member-id" value={input} maxLength={200} placeholder="Enter exact Member ID"
          onChange={event => { setInput(event.target.value); setMemberId(null); setError(''); }} />
      </Field>
      <Button type="submit" label="Find Member Flags" icon="pi pi-search" />
    </form>
    <p className="flag-note">Member IDs retain case and leading zeros. The first successful flag assignment registers an identifier-only flag-system record; there is no standalone member-create endpoint here.</p>
    {catalog.error && <ResourceState resource={catalog} />}
    {catalog.data && !catalog.data.some(flag => flag.active !== false) && <Message severity="info" text="No flags are available. Create active flags in Flag Management first." />}
    {memberId && <MemberAssignments key={`${memberId}:${lookupVersion}`} account={account} scope={scope} memberId={memberId}
      catalog={catalog} onAccessError={onAccessError} />}
  </>;
};

const ScanBatches = ({ customerId, tileId, filters, allowed, onAccessError }) => {
  const [page, setPage] = useState(0);
  const [records, setRecords] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 100;
  const loader = useCallback(async () => ({
    page, records: await flagHistoryApi.list(customerId, tileId, { ...filters, page, pageSize }),
  }), [customerId, tileId, filters, page]);
  const resource = useRemoteResource(loader, onAccessError);
  useEffect(() => {
    if (!resource.data) return;
    const batch = resource.data;
    setRecords(previous => {
      if (batch.page === 0) return batch.records;
      const combined = new Map(previous.map(record => [record.id, record]));
      batch.records.forEach(record => combined.set(record.id, record));
      return Array.from(combined.values());
    });
    setHasMore(batch.records.length === pageSize);
  }, [resource.data]);
  const filtered = records.filter(record => record.allowed === allowed);
  return <>
    <p className="flag-note">{filtered.length} {allowed ? 'allowed' : 'denied'} records in {records.length} loaded historical records (both results combined). These are not all-time totals or a live decision stream. Times shown in Europe/London.</p>
    {resource.loading && <Message severity="info" text="Loading historical records..." />}
    {resource.error && <div className="flex align-items-center gap-2 mb-3"><Message severity="error" text={resource.error} />
      <Button label="Retry batch" onClick={resource.reload} /></div>}
    <DataTable value={filtered} dataKey="id" emptyMessage={resource.loading ? 'Loading...' : 'No matching scans in the loaded batches.'} responsiveLayout="scroll">
      <Column header="Time" body={record => formatLegacyScan(record.time)} />
      <Column field="number" header="Member ID" />
      <Column header="Tile / Area" body={record => record.accessPoint && record.accessPoint.gate ? record.accessPoint.gate : tileId} />
      <Column field="scanType" header="Scan Type" />
      <Column header="Result" body={() => <Tag value={allowed ? 'Allowed' : 'Denied'} severity={allowed ? 'success' : 'danger'} />} />
    </DataTable>
    {hasMore && <Button label="Load more historical records" icon="pi pi-chevron-down" className="mt-3"
      disabled={resource.loading || Boolean(resource.error)} onClick={() => setPage(value => value + 1)} />}
    {!allowed && <Unavailable>Historical logs do not supply triggered flags, assignment IDs, role-sensitive denial causes or per-event override eligibility. Today's flags must not be used to infer a past denial.</Unavailable>}
  </>;
};

const HistoricalScans = ({ account, scope, allowed, onAccessError }) => {
  const loader = useCallback(() => flagLookupsApi.tiles(scope), [scope]);
  const tiles = useRemoteResource(loader, onAccessError);
  const [selectedTile, setSelectedTile] = useState(null);
  const [dates, setDates] = useState({ initDate: '', endDate: '', membership: '' });
  const [filters, setFilters] = useState({});
  const [version, setVersion] = useState(0);
  const [error, setError] = useState('');
  const tileId = (tiles.data || []).some(tile => tile.tileId === selectedTile) ? selectedTile : null;
  const apply = (event) => {
    event.preventDefault();
    if (dates.initDate && dates.endDate && dates.initDate > dates.endDate) { setError('Start date must not be after end date.'); return; }
    setError('');
    setFilters(Object.keys(dates).reduce((query, key) => dates[key].trim() ? { ...query, [key]: dates[key].trim() } : query, {}));
    setVersion(value => value + 1);
  };
  return <>
    <ResourceState resource={tiles}>
      <Field id="history-tile" label="Tile">
        <Dropdown inputId="history-tile" value={tileId} options={tiles.data || []} optionLabel="gateName" optionValue="tileId"
          placeholder="Select a tile for historical scans" onChange={event => setSelectedTile(event.value)} />
      </Field>
      {tiles.data && !tiles.data.length && <p>No tiles are available for this location.</p>}
    </ResourceState>
    {tileId && <>
      <form onSubmit={apply}>
        <div className="flag-grid">{[['initDate', 'Start date'], ['endDate', 'End date']].map(([field, label]) =>
          <Field key={field} id={`history-${field}`} label={label}>
            <InputText id={`history-${field}`} type="date" value={dates[field]} onChange={event => setDates(previous => ({ ...previous, [field]: event.target.value }))} />
          </Field>)}</div>
        <Field id="history-membership" label="Member identifier substring (historical filter)">
          <InputText id="history-membership" value={dates.membership} onChange={event => setDates(previous => ({ ...previous, membership: event.target.value }))} />
        </Field>
        <Button type="submit" label="Apply Filters / Refresh" icon="pi pi-filter" className="mb-3" />
      </form>
      <Feedback error={error} />
      <ScanBatches key={`${tileId}:${version}`} customerId={account.customerId} tileId={tileId} filters={filters}
        allowed={allowed} onAccessError={onAccessError} />
    </>}
    <Unavailable>A live decision stream, role-sensitive flag scan feed and manual check-in API are not available. No random scans are generated in production.</Unavailable>
  </>;
};

const ScopedSiteViewer = ({ account, locationId, onAccessError }) => {
  const [activeTab, setActiveTab] = useState(0);
  const scope = React.useMemo(() => ({ customerId: account.customerId, locationId }), [account.customerId, locationId]);
  return <>
    <TabView activeIndex={activeTab} onTabChange={event => setActiveTab(event.index)}>
      <TabPanel header="Member Search" />
      <TabPanel header="Allowed Scans" />
      <TabPanel header="Denied Scans" />
    </TabView>
    <div hidden={activeTab !== 0}><MemberSearch account={account} scope={scope} onAccessError={onAccessError} /></div>
    <div hidden={activeTab === 0}><HistoricalScans account={account} scope={scope} allowed={activeTab === 1} onAccessError={onAccessError} /></div>
  </>;
};
const SiteViewer = ({ account, onAccessError }) => {
  const loader = useCallback(() => flagLookupsApi.locations(account.customerId), [account.customerId]);
  const resource = useRemoteResource(loader, onAccessError);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const locations = resource.data || [];
  const locationId = locations.length === 1 ? locations[0].id :
    (locations.some(location => location.id === selectedLocation) ? selectedLocation : null);
  return <FlagPage title="Site Viewer">
    <ResourceState resource={resource}>
      <Field id="flag-location" label="Location">
        <Dropdown inputId="flag-location" value={locationId} options={locations} optionLabel="name" optionValue="id"
          placeholder="Select one of your customer locations" onChange={event => setSelectedLocation(event.value)} />
      </Field>
      {!locations.length && <p>No locations are available for your customer.</p>}
      {locationId && <ScopedSiteViewer key={locationId} account={account} locationId={locationId} onAccessError={onAccessError} />}
    </ResourceState>
  </FlagPage>;
};
export default SiteViewer;
